import { useState } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAjusteManual } from "../hooks";
import type { InventoryMovementType } from "@cold-crew/shared";

interface AjusteModalProps {
  productoId:     number;
  productoNombre: string;
  unidad:         string;
  onClose:        () => void;
}

const TIPOS: { value: Extract<InventoryMovementType, "entrada" | "salida" | "ajuste" | "merma">; label: string }[] = [
  { value: "entrada", label: "Entrada de stock" },
  { value: "salida",  label: "Salida manual"    },
  { value: "ajuste",  label: "Ajuste / corrección" },
  { value: "merma",   label: "Merma / pérdida"  },
];

const inputCls = "h-9 w-full rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors";

export function AjusteModal({ productoId, productoNombre, unidad, onClose }: AjusteModalProps) {
  const [tipo,     setTipo]     = useState<typeof TIPOS[number]["value"]>("entrada");
  const [cantidad, setCantidad] = useState("");
  const [notas,    setNotas]    = useState("");
  const [error,    setError]    = useState<string | null>(null);
  const ajuste = useAjusteManual();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const qty = parseFloat(cantidad);
    if (!qty || qty <= 0) { setError("La cantidad debe ser mayor a cero."); return; }
    try {
      await ajuste.mutateAsync({ producto_id: productoId, tipo, cantidad: qty, notas: notas.trim() || undefined });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al guardar el ajuste.");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-card rounded-lg border border-border shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-base font-semibold">Ajuste de inventario</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{productoNombre}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-muted text-muted-foreground transition-colors">
            <X className="size-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">Tipo de movimiento</label>
            <select value={tipo} onChange={(e) => setTipo(e.target.value as typeof tipo)}
              className={cn(inputCls, "cursor-pointer")}>
              {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Cantidad ({unidad})</label>
            <input type="number" min={0.01} step="0.01" placeholder="0"
              value={cantidad} onChange={(e) => setCantidad(e.target.value)}
              className={inputCls} required />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Notas <span className="text-muted-foreground font-normal">(opcional)</span></label>
            <input type="text" placeholder="Motivo del ajuste…"
              value={notas} onChange={(e) => setNotas(e.target.value)} className={inputCls} />
          </div>

          {error && <p className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 h-9 rounded-md border border-border text-sm hover:bg-muted transition-colors">
              Cancelar
            </button>
            <button type="submit" disabled={ajuste.isPending}
              className="flex-1 h-9 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {ajuste.isPending && <span className="size-3.5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />}
              Guardar ajuste
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
